import React, { useMemo } from "react";
import { TopPost } from "../hooks/useSocialMediaReport";
import { EditorPostCard } from "@/domains/PostCreator/cards/EditorPostCard";
import { ListPostCard } from "@/domains/PostCreator/cards/ListPostCard";
import type { StatsLocale } from "@/components/PostCard";
import { PostItem } from "@/core/Post/PostItem.model";
import type { Post, PostStatus } from "@/core/types/postTypes";
import { usePostCardAuthOptional } from "@/domains/PostCreator/contexts/PostCardAuthContext";
import { UserRole } from "@/services/urlService";

interface TopPostsSectionProps {
  topPosts: TopPost[];
  /** Render top posts as a grid of EditorPostCards (admin /reporting)
   *  or as a stack of ListPostCards (client portal /report). Default
   *  is grid for back-compat. */
  variant?: "grid" | "list";
  /** Copy locale flows through to each card and the section header. */
  locale?: StatsLocale;
}

const SECTION_COPY: Record<StatsLocale, {
  title: string;
  subtitle: (n: number) => string;
}> = {
  en: {
    title: "Top Posts",
    subtitle: (n) => `${n} best performing`,
  },
  it: {
    title: "Migliori Post",
    subtitle: (n) => `top ${n}`,
  },
};

/**
 * Top posts of a report period. Each card is the exact same
 * EditorPostCard rendered on /posts (same media pipeline, same
 * caption block, same stats panel). The reporting view doesn't add
 * any bespoke chrome — that's the whole point of reusing the component.
 *
 * Posts without an internal match (rare; mostly boosted ads or posts
 * published outside this system) are silently skipped because we
 * can't drive EditorPostCard without a real Post entity.
 */
export const TopPostsSection: React.FC<TopPostsSectionProps> = ({
  topPosts,
  variant = "grid",
  locale = "en",
}) => {
  const copy = SECTION_COPY[locale];
  // Require a hydrated internal Post. Old cached reports (pre-grouping
  // rollout) stored a raw FB API payload under `post`; that payload
  // happens to carry an `id`, so a basic null/id check still passes
  // it through. The distinguishing markers are `media_items` (array,
  // present on internal Post.to_dict()) and `status` (PostStatus enum).
  // Neither exists on a platform payload, so we use them as the
  // hydration signal.
  const renderable = useMemo(
    () =>
      topPosts.filter(
        (tp) =>
          tp.post != null &&
          Array.isArray((tp.post as Post & { media_items?: unknown }).mediaItems),
      ),
    [topPosts],
  );

  const containerClass =
    variant === "list"
      ? "flex flex-col gap-3"
      : "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3";

  // Post cards carry their own chrome, so this section sits directly on
  // the canvas (DD-008 language) — no panel around it.
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-3 px-1">
        <h2 className="text-sm font-semibold text-gray-900">{copy.title}</h2>
        {renderable.length > 0 && (
          <span className="text-xs text-gray-400">
            {copy.subtitle(Math.min(renderable.length, 9))}
          </span>
        )}
      </div>
      {renderable.length === 0 ? (
        <p className="text-sm text-gray-500 px-1 py-4">
          No published posts to rank for this month.
        </p>
      ) : (
        <div className={containerClass}>
          {renderable.slice(0, 9).map((item) => (
            <TopPostCard
              key={item.postId ?? item.post?.id}
              item={item}
              variant={variant}
              locale={locale}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TopPostCard: React.FC<{
  item: TopPost;
  variant: "grid" | "list";
  locale: StatsLocale;
}> = ({ item, variant, locale }) => {
  // Pick the auth context based on where the section is rendered:
  // admin app → no PostCardAuthProvider, default to "admin".
  // Client portal → PostCardAuthProvider with role=CLIENT + token,
  // which the underlying MediaItem pipeline uses to sign streaming
  // URLs. Without this hook the client portal would 401 on media.
  const authContext = usePostCardAuthOptional();
  const context: "admin" | "client" =
    authContext?.userRole === UserRole.CLIENT ? "client" : "admin";
  const authToken = authContext?.authToken;

  // Convert the raw post dict (snake_case, media_items as plain data)
  // into the frontend Post shape (camelCase getters + MediaItem
  // instances) so EditorPostCard's media pipeline works. In client
  // context, MediaItem.fromBackend requires an explicit auth token to
  // sign streaming URLs — pull it from PostCardAuthContext and forward.
  const post = useMemo<Post | null>(() => {
    if (!item.post) return null;
    return PostItem.fromLegacyPost(
      item.post,
      context,
      item.post.clientId,
      authToken,
    ).toLegacyPost() as Post;
  }, [item.post, context, authToken]);

  if (!post) return null;

  // Permalink overrides: backend stores them per (post, platform) on
  // the report's `platforms` field. Forward them to EditorPostCard
  // so the platform badges in the stats panel become "view on FB /
  // IG / TikTok" links instead of dead icons. Keys are lowercase to
  // match PLATFORM_REGISTRY. Defaults to empty when the report was
  // generated before the grouped-shape rollout (`platforms` absent).
  const permalinks: Partial<Record<string, string | null | undefined>> = {};
  if (item.platforms) {
    for (const [k, entry] of Object.entries(item.platforms)) {
      permalinks[k] = entry?.post?.permalink ?? null;
    }
  }

  if (variant === "list") {
    return (
      <ListPostCard
        post={post}
        onStatusChange={NOOP_STATUS_CHANGE}
        onPostDeleted={NOOP}
        statsPermalinks={permalinks}
        locale={locale}
      />
    );
  }

  return (
    <EditorPostCard
      post={post}
      onStatusChange={NOOP_STATUS_CHANGE}
      onPostDeleted={NOOP}
      statsPermalinks={permalinks}
    />
  );
};

const NOOP = () => {};
const NOOP_STATUS_CHANGE = (_p: Post, _s: PostStatus, _r: unknown) => {};
